import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";

import VillaOverview from "../components/VillaOverview";
import RoomCategorySelector from "../components/RoomCategorySelector";
import RoomDetails from "../components/RoomDetails";
import NeighbourhoodInfo from "../components/NeighbourhoodInfo";
import { RoomCategories } from "../configs";
import { openWhatsAppOrCall } from "../utils/directBooking";

function Home() {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToRoomCategories = () => {
    const element = document.getElementById("room-categories");
    if (!element) return;

    // Keep the section title right below the top nav without exposing the previous section.
    const topOffset = 80;
    const elementTop =
      element.getBoundingClientRect().top + window.pageYOffset - topOffset;

    window.scrollTo({
      top: Math.max(0, elementTop),
      behavior: "smooth",
    });
  };

  // Get room category from URL parameter or default to first room
  const getInitialCategory = () => {
    const params = new URLSearchParams(location.search);
    const roomParam = params.get("room");
    if (roomParam && RoomCategories.find((cat) => cat.id === roomParam)) {
      return roomParam;
    }
    return RoomCategories[0]?.id || "villa";
  };

  const [selectedCategory, setSelectedCategory] =
    useState(getInitialCategory());
  const [isMapLoading, setIsMapLoading] = useState(true);

  // Update URL when category changes
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    navigate(`/?room=${categoryId}`, { replace: true });

    scrollToRoomCategories();
  };

  // Update selected category when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roomParam = params.get("room");
    if (roomParam && RoomCategories.find((cat) => cat.id === roomParam)) {
      setSelectedCategory(roomParam);

      // Scroll to room categories section after a brief delay
      setTimeout(() => {
        scrollToRoomCategories();
      }, 100);
    }
  }, [location.search]);

  return (
    <div>
      {/* Villa Overview - Common images, description (no map) */}
      <VillaOverview />

      <div className="px-3 mt-4">
        <div className="rounded-xl border border-green-200 bg-gradient-to-r from-green-700 via-green-600 to-emerald-500 p-4 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-lg font-bold text-white leading-tight">
                Book Direct on WhatsApp and Get the Best Price
              </p>
              <p className="text-sm text-green-50 mt-1">
                Fast confirmation, direct support, and our most appealing rates.
              </p>
            </div>
            <button
              type="button"
              onClick={() => openWhatsAppOrCall()}
              className="shrink-0 px-4 py-2 rounded-lg bg-white text-green-700 font-semibold hover:bg-green-50 transition-colors"
            >
              Chat & Book Now
            </button>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div
        id="room-categories"
        className="scroll-mt-24 mt-2 mb-3 px-3 text-center bg-primary-main py-6 rounded-lg shadow-md"
      >
        <h2 className="text-2xl font-bold text-secondary-main mb-2">
          Explore Our Rooms
        </h2>
        <p className="text-md text-secondary-dark">
          Choose from our selection of comfortable accommodations
        </p>
      </div>

      {/* Room Category Selector - Sticky navigation */}
      <RoomCategorySelector
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />

      {/* Room-specific details (gallery, features, description) */}
      <RoomDetails categoryId={selectedCategory} />

      {/* Map Section - Moved here for better organization */}
      <div className="mt-6 px-3">
        <div className="bg-primary-main rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-secondary-main mb-3">
            Location
          </h2>
          <p className="text-sm text-secondary-dark mb-4">
            Find us in the heart of Galle, close to major attractions
          </p>
          <div
            className="relative w-full rounded-lg overflow-hidden shadow-md"
            style={{ height: "400px" }}
          >
            {isMapLoading && (
              <div className="image-skeleton absolute inset-0" />
            )}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3967.6836085701552!2d80.21143540972972!3d6.038081793922277!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae1730054a0da9b%3A0x6d12e87b2f017c9b!2sNutmeg%20Grove!5e0!3m2!1sen!2slk!4v1731661704367!5m2!1sen!2slk"
              width="100%"
              height="100%"
              style={{ border: "0" }}
              loading="lazy"
              onLoad={() => setIsMapLoading(false)}
              title="Property Location"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Neighbourhood information */}
      <NeighbourhoodInfo />

      {/* Professional guides teaser */}
      <div className="mt-6 px-3 pb-2">
        <div className="rounded-xl border border-primary-medium/70 bg-gradient-to-br from-secondary-main via-secondary-medium to-secondary-dark shadow-xl p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-light/90">
                <MenuBookOutlinedIcon fontSize="small" />
                Local Concierge Guides
              </p>
              <p className="text-xl font-bold text-brand-light mt-1">
                Build Your Perfect Galle Plan
              </p>
              <p className="text-sm text-brand-light/90 mt-2 max-w-xl leading-relaxed">
                Discover curated routes and attraction ideas with practical
                planning notes so guests can prioritize what matters most.
              </p>
            </div>
            <Link
              to="/guides"
              className="inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-main text-secondary-main hover:bg-brand-light transition-colors"
            >
              Open Guides
              <ArrowForwardOutlinedIcon fontSize="small" />
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-md bg-brand-light/10 border border-brand-light/15 p-3">
              <p className="text-xs text-brand-light/80 uppercase tracking-wide inline-flex items-center gap-1">
                <ExploreOutlinedIcon fontSize="inherit" />
                Popular Keywords
              </p>
              <p className="text-sm text-brand-light mt-1">
                Beaches, sunset, cafes, heritage, family
              </p>
            </div>
            <div className="rounded-md bg-brand-light/10 border border-brand-light/15 p-3">
              <p className="text-xs text-brand-light/80 uppercase tracking-wide inline-flex items-center gap-1">
                <FilterAltOutlinedIcon fontSize="inherit" />
                Planning Mode
              </p>
              <p className="text-sm text-brand-light mt-1">
                Filter by vibe, duration, and must-do highlights
              </p>
            </div>
            <div className="rounded-md bg-brand-light/10 border border-brand-light/15 p-3">
              <p className="text-xs text-brand-light/80 uppercase tracking-wide inline-flex items-center gap-1">
                <MenuBookOutlinedIcon fontSize="inherit" />
                Ready Itineraries
              </p>
              <p className="text-sm text-brand-light mt-1">
                1-day, 2-day, 3-day routes from Nutmeg Grove
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
